import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import {
  bindInputProps,
  bindWebTextFieldInputProps,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import useAcorisZipAddress from "../utils/useAcorisZipAddress";
import {
  ACORIS_ZIP_CODE_LENGTH,
  getAcorisCityPlaceholder,
} from "../utils/zipCode";
import validation from "./validation";

function View({ telecoms, onNext, onBack }) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const translate = t("ADHERENT_TELECOMS", { returnObjects: true });
  const { t: processesT } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = processesT("DETAIL.COUNTRIES", { returnObjects: true });
  const countryOptions = useMemo(
    () =>
      (Array.isArray(COUNTRIES) ? COUNTRIES : []).map((name) => ({
        value: name,
        label: name,
      })),
    [COUNTRIES],
  );

  const { submitForm, values, setFieldValue, ...formProps } = useFormik({
    initialValues: { telecoms },
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });
  const zipCode = get(
    values,
    `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
  );
  const { isForeignZip, cities, citiesLoading } = useAcorisZipAddress(zipCode);

  return (
    <Layout>
      <Header
        title={translate?.HEADER?.TITLE}
        highlight={translate?.HEADER?.HIGHLIGHT}
        subtitle={translate?.HEADER?.SUBTITLE}
        onBack={onBack}
      />
      <div className="acoris-form">
        <div className="acoris-form__container">
          <div className="acoris-form__row acoris-form__row--single">
            <Input
              placeholder={translate?.FORM?.ADDRESS?.PLACEHOLDER}
              label={translate?.FORM?.ADDRESS?.LABEL}
              {...bindWebTextFieldInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                values,
                ...formProps,
              })}
            />
          </div>
          <div className="acoris-form__row">
            <Input
              disabled
              maxLength={ACORIS_ZIP_CODE_LENGTH}
              placeholder={translate?.FORM?.ZIP_CODE?.PLACEHOLDER}
              label={translate?.FORM?.ZIP_CODE?.LABEL}
              {...bindInputProps({
                name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                values,
                ...formProps,
              })}
            />
            {isForeignZip ? (
              <Input
                key="city-free"
                placeholder={getAcorisCityPlaceholder(
                  translate?.FORM?.CITY,
                  true,
                )}
                label={translate?.FORM?.CITY?.LABEL}
                {...bindWebTextFieldInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
              />
            ) : (
              <Select
                key="city-select"
                showSearch
                loading={citiesLoading}
                options={cities}
                placeholder={translate?.FORM?.CITY?.PLACEHOLDER}
                label={translate?.FORM?.CITY?.LABEL}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                  values,
                  ...formProps,
                })}
              />
            )}
          </div>
          {isForeignZip && (
            <div className="acoris-form__row acoris-form__row--single">
              <Select
                showSearch
                options={countryOptions}
                placeholder={translate?.FORM?.COUNTRY?.PLACEHOLDER}
                label={translate?.FORM?.COUNTRY?.LABEL}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
                  values,
                  ...formProps,
                })}
              />
            </div>
          )}
          <ActionButtons
            disabled={
              !get(values, `telecoms.${CONTACT_POINT_SYSTEM.ADDRESS}.value`) ||
              !isEmpty(formProps.errors)
            }
            onNext={submitForm}
            onBack={onBack}
            buttonText={translate?.BUTTON?.CONTINUE}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
